/*
 * Raw Delights — Desktop cart popover enhancement
 *
 * The compact confirmation block inside the cart drawer is server-rendered
 * on every add (Section Rendering API), using the last line in cart.items
 * as a reasonable default for "what was just added". That default is wrong
 * in one case: increasing the quantity of a product already in the cart
 * doesn't move it to the end of cart.items. This script listens for the
 * same add-to-cart events every custom section on the site already fires
 * (cart:updated) plus Shopify's native CartLinesUpdateEvent, and corrects
 * the confirmation block's item row using the real payload when available.
 * Pure enhancement — if no event payload is available the server-rendered
 * default (last cart line) is left as-is.
 */
(() => {
  if (window.__rdCartPopoverApplied) return;
  window.__rdCartPopoverApplied = true;

  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP',
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `£${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  const updateConfirmBlock = (item, cart) => {
    if (!item) return;
    const confirm = document.querySelector('[data-rd-cart-popover-confirm]');
    if (!confirm) return;

    const titleEl = confirm.querySelector('[data-rd-cart-popover-item-title]');
    const variantEl = confirm.querySelector('[data-rd-cart-popover-item-variant]');
    const qtyEl = confirm.querySelector('[data-rd-cart-popover-item-qty]');
    const priceEl = confirm.querySelector('[data-rd-cart-popover-item-price]');
    const imageWrap = confirm.querySelector('[data-rd-cart-popover-last-item] .rd-cart-popover__item-media');
    const countEl = confirm.querySelector('[data-rd-cart-popover-count]');
    const subtotalEl = confirm.querySelector('[data-rd-cart-popover-subtotal]');

    const productTitle = item.product_title || item.title || '';
    const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
    const quantity = Number(item.quantity || item.final_quantity || 1);
    const linePrice = Number(item.final_line_price ?? item.line_price ?? (item.price || 0) * quantity);

    if (titleEl && productTitle) titleEl.textContent = productTitle;
    if (variantEl) {
      if (variantTitle) {
        variantEl.textContent = variantTitle;
        variantEl.hidden = false;
      } else {
        variantEl.hidden = true;
      }
    }
    if (qtyEl) qtyEl.textContent = `Quantity: ${quantity}`;
    if (priceEl) priceEl.textContent = formatMoney(linePrice);

    const imageUrl = item.image || (item.featured_image && item.featured_image.url);
    if (imageWrap && imageUrl) {
      imageWrap.innerHTML = `<img class="rd-cart-popover__item-image" src="${imageUrl}" alt="${(productTitle || '').replace(/"/g, '&quot;')}" loading="lazy">`;
    }

    if (cart) {
      if (countEl) countEl.textContent = cart.item_count === 1 ? '1 item' : `${cart.item_count} items`;
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    }
  };

  document.addEventListener('cart:updated', (event) => {
    const detail = event.detail || {};
    updateConfirmBlock(detail.item, detail.cart);
  });

  // Native Shopify add-to-cart flows (PDP buy buttons, product-card quick add)
  // dispatch CartLinesUpdateEvent directly rather than our custom cart:updated.
  document.addEventListener('cart-lines-update', async (event) => {
    try {
      const detail = event.detail || {};
      const resolved = detail.promise ? await detail.promise : detail;
      const items = resolved?.detail?.items || resolved?.items;
      if (Array.isArray(items) && items.length) {
        updateConfirmBlock(items[items.length - 1], resolved.cart || resolved.detail?.cart);
      }
    } catch (error) {
      // Server-rendered default already covers this — no user-facing impact.
    }
  });
})();
